/**
 * Karte „Telegram" unter Einstellungen → Verbindungen. (29.09.2026, Ticket 2819)
 *
 * Nur Konto 1 (die Seite zeigt sie nur dort; der Server antwortet anderen
 * mit 403). Gekoppelt wird ausschließlich über einen Einmal-Code, den man
 * dem Bot schreibt — am bequemsten über den t.me-Link, der „/start CODE"
 * selbst einträgt. Dieselben Bausteine wie die Karten „Chrome-Extension"
 * und „Vespera Desktop" (settings-extension, connector-*).
 */

export type TelegramModus = "aus" | "ersatz" | "immer";

export interface TelegramStand {
  ok: boolean;
  kanal: boolean;
  gekoppelt: boolean;
  modus: TelegramModus;
  bot: string | null;
}

const MODUS_TEXT: Record<TelegramModus, string> = {
  ersatz: "Nur wenn die Mitteilung aufs Telefon nicht ankommt",
  immer: "Immer zusätzlich zur Mitteilung",
  aus: "Nie von selbst — nur wenn ich darum bitte",
};

/** Der Link, der dem Bot „/start CODE" schickt. */
export function startLink(bot: string, code: string): string {
  return `https://t.me/${encodeURIComponent(bot)}?start=${encodeURIComponent(code)}`;
}

/** Die Statuszeile — ein Satz, keine Technik. */
export function standText(s: TelegramStand): { text: string; ton: "is-ok" | "is-warn" | "is-off" } {
  if (!s.kanal) return { text: "Noch nicht eingerichtet — auf dem Server fehlt der Bot-Schlüssel", ton: "is-off" };
  if (!s.gekoppelt) return { text: "Bereit — noch nicht mit deinem Telegram gekoppelt", ton: "is-warn" };
  return { text: s.bot ? `Verbunden mit @${s.bot}` : "Verbunden", ton: "is-ok" };
}

async function holeStand(): Promise<TelegramStand | null> {
  try {
    const r = await fetch("/api/telegram/stand", { credentials: "same-origin" });
    return r.ok ? ((await r.json()) as TelegramStand) : null;
  } catch {
    return null;
  }
}

async function post<T>(pfad: string, daten?: unknown): Promise<T | null> {
  try {
    const r = await fetch(pfad, {
      method: "POST",
      credentials: "same-origin",
      headers: daten ? { "Content-Type": "application/json" } : undefined,
      body: daten ? JSON.stringify(daten) : undefined,
    });
    return r.ok ? ((await r.json()) as T) : null;
  } catch {
    return null;
  }
}

export function createTelegramKarte(): HTMLElement {
  const wrap = document.createElement("section");
  wrap.className = "settings-extension telegram-karte";

  const head = document.createElement("div");
  head.className = "settings-extension-head";
  const titel = document.createElement("h3");
  titel.textContent = "Telegram";
  head.appendChild(titel);
  wrap.appendChild(head);

  const desc = document.createElement("p");
  desc.className = "settings-extension-desc";
  desc.textContent =
    "Vespera schreibt dir auf Telegram, wenn dich eine Meldung sonst nicht erreicht, und du kannst ihr dort schreiben wie im Chat.";
  wrap.appendChild(desc);

  const status = document.createElement("div");
  status.className = "connector-status";
  status.setAttribute("role", "status");
  status.textContent = "Status wird geladen …";
  wrap.appendChild(status);

  const codeBox = document.createElement("div");
  codeBox.className = "connector-code-box";
  codeBox.hidden = true;
  wrap.appendChild(codeBox);

  const wahlZeile = document.createElement("label");
  wahlZeile.className = "einst-feld einst-feld--wahl";
  wahlZeile.hidden = true;
  const wahlTitel = document.createElement("span");
  wahlTitel.className = "einst-feld-titel";
  wahlTitel.textContent = "Wann auf Telegram melden";
  wahlZeile.appendChild(wahlTitel);
  const wahl = document.createElement("select");
  wahl.className = "einst-wahl";
  for (const m of ["ersatz", "immer", "aus"] as const) {
    const o = document.createElement("option");
    o.value = m;
    o.textContent = MODUS_TEXT[m];
    wahl.appendChild(o);
  }
  wahlZeile.appendChild(wahl);
  wrap.appendChild(wahlZeile);

  const actions = document.createElement("div");
  actions.className = "connector-actions";
  const koppeln = document.createElement("button");
  koppeln.type = "button";
  koppeln.className = "connector-connect";
  koppeln.textContent = "Mit Telegram koppeln";
  const trennen = document.createElement("button");
  trennen.type = "button";
  trennen.className = "connector-disconnect";
  trennen.textContent = "Trennen";
  actions.append(koppeln, trennen);
  wrap.appendChild(actions);

  const zeige = (s: TelegramStand | null): void => {
    while (status.firstChild) status.removeChild(status.firstChild);
    if (!s) {
      status.textContent = "Status gerade nicht abrufbar.";
      koppeln.hidden = true;
      trennen.hidden = true;
      wahlZeile.hidden = true;
      return;
    }
    const { text, ton } = standText(s);
    const dot = document.createElement("span");
    dot.className = `connector-dot ${ton}`;
    const txt = document.createElement("span");
    txt.textContent = text;
    status.append(dot, txt);
    koppeln.hidden = !s.kanal || s.gekoppelt;
    trennen.hidden = !s.gekoppelt;
    wahlZeile.hidden = !s.gekoppelt;
    wahl.value = s.modus;
    if (s.gekoppelt) codeBox.hidden = true;
  };

  const koppelnKlick = async (): Promise<void> => {
    koppeln.disabled = true;
    const r = await post<{ ok: boolean; code: string; bot: string | null }>("/api/telegram/code");
    koppeln.disabled = false;
    while (codeBox.firstChild) codeBox.removeChild(codeBox.firstChild);
    codeBox.hidden = false;
    if (!r?.ok) {
      codeBox.textContent = "Der Code ließ sich gerade nicht erzeugen.";
      return;
    }
    const gross = document.createElement("div");
    gross.className = "connector-code-big";
    gross.textContent = r.code;
    codeBox.appendChild(gross);
    const hinweis = document.createElement("div");
    hinweis.className = "connector-code-hint";
    if (r.bot) {
      const link = document.createElement("a");
      link.href = startLink(r.bot, r.code);
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.textContent = `In Telegram öffnen (@${r.bot})`;
      hinweis.append(link, document.createTextNode(` — oder schreibe dem Bot „/start ${r.code}“. Der Code gilt 10 Minuten.`));
    } else {
      hinweis.textContent = `Schreibe dem Bot von Vespera „/start ${r.code}“. Der Code gilt 10 Minuten.`;
    }
    codeBox.appendChild(hinweis);
    // Nachsehen, ob gekoppelt wurde — höchstens 10 Minuten lang.
    const bis = Date.now() + 10 * 60_000;
    const nachsehen = async (): Promise<void> => {
      if (Date.now() > bis || !wrap.isConnected) return;
      const s = await holeStand();
      if (s?.gekoppelt) {
        zeige(s);
        return;
      }
      setTimeout(() => void nachsehen(), 4000);
    };
    setTimeout(() => void nachsehen(), 4000);
  };
  koppeln.addEventListener("click", () => void koppelnKlick());

  const trennenKlick = async (): Promise<void> => {
    const { confirmSheet } = await import("./sheet-ui.js");
    const ok = await confirmSheet({ title: "Telegram trennen?", confirmLabel: "Trennen", danger: true });
    if (!ok) return;
    await post("/api/telegram/trennen");
    zeige(await holeStand());
  };
  trennen.addEventListener("click", () => void trennenKlick());

  const modusWahl = async (): Promise<void> => {
    const r = await post<{ ok: boolean }>("/api/telegram/modus", { modus: wahl.value });
    if (!r?.ok) zeige(await holeStand());
  };
  wahl.addEventListener("change", () => void modusWahl());

  void holeStand().then(zeige);
  return wrap;
}
